import type React from 'react'
import { useState, useContext } from 'react'
import styled from 'styled-components'

import Button from '@/components/Button'
import SlideInSidebar from '@/components/DEPRECATED_UI/SlideInSidebar'
import H2 from '@/components/H2'
import P from '@/components/P'
import ctx from '@/context/AppContext'
import { graphQLQuery } from '@/hooks/hooks'
import QUERIES from '@/lib/graphql'
import type { Application } from '@/types/Activity'
import type { Touchpoint } from '@/types/Touchpoints'
import { formatDate } from '@/utils/date'

import SendMessageForm from './SendMessageForm'

interface Props {
  application: Application
  isOpen: boolean
  onClose: () => void
  messages: Touchpoint[]
  onPostCommentSuccess: () => void
}

const MessagesContainer = styled.div`
  overflow-y: auto;
  display: grid;
  gap: 50px;
  align-items: start;
  grid-auto-rows: min-content;
`

const AuthorDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr min-content;
  gap: 10px;
`

const Body = styled.div`
  padding-top: 20px;
  height: 100%;
  display: grid;
  grid-gap: 20px;
  grid-template-rows: min-content min-content 1fr;
`

const StyledButton = styled(Button)`
  margin: 0;
`

const Touchpoints: React.FC<Props> = ({
  messages,
  onPostCommentSuccess,
  isOpen,
  onClose,
  application,
}) => {
  const { personId } = useContext(ctx)
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [message, setMessage] = useState('')

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (message.trim().length === 0) return
    postNewComment(message)
    setMessage('')
    setIsEditing(false)
  }

  async function postNewComment(message: string) {
    const { customerId, applicationId, subjectId } = application
    const res = await graphQLQuery({
      query: QUERIES.addTouchpoint,
      variables: {
        touchpoint: {
          customerId: customerId,
          applicationId: applicationId,
          subjectId: subjectId,
          type: 'internal-communication',
          message,
          author: {
            id: personId,
            type: 'person',
          },
        },
      },
    })
    const data = await res.json()
    if (data.data.addTouchpoint.id) {
      onPostCommentSuccess()
    }
  }

  return isOpen ? (
    <SlideInSidebar
      onClose={onClose}
      header={<H2>Messages</H2>}
      content={
        <Body>
          <P>
            These comments will not be visible to the agent within the Goodlord dashboard.
          </P>
          <div>
            {isEditing ? (
              <SendMessageForm
                message={message}
                onChangeMessage={setMessage}
                handleSubmit={handleSubmit}
                onClose={() => setIsEditing(false)}
              />
            ) : (
              <StyledButton type="button" onClick={() => setIsEditing(true)}>
                Leave a message
              </StyledButton>
            )}
          </div>
          <MessagesContainer>
            {messages &&
              messages.length > 0 &&
              messages.map(({ message, author, createdAt }, i) => (
                <div key={i}>
                  <AuthorDetails>
                    <P>
                      <strong>
                        {personId === author.id ? <>You</> : author.details?.email}
                      </strong>
                    </P>
                    <P>{formatDate(new Date(createdAt), 'dd/MM/yyyy HH:mm')}</P>
                  </AuthorDetails>
                  <P>{message}</P>
                </div>
              ))}
            {messages.length === 0 && (
              <p>Nothing here yet! Be the first one to post a message.</p>
            )}
          </MessagesContainer>
        </Body>
      }
    />
  ) : (
    <></>
  )
}

export default Touchpoints
